import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from '@tanstack/react-query'
import { Check, Undo2, Zap } from 'lucide-react'

import {
  trackUndoResult,
  trackUndoShown,
  trackUndoTapped,
  undoableNow,
  type ReceiptVM,
} from '@/lib/receipts'
import { undoEricAction } from '@/lib/threads'
import { mapUndoError, undoErrorCode } from '@/lib/undo-errors'

/**
 * The ONE "system did work" artifact (DESIGN.md §4.1, §6, §2).
 *
 * Anatomy: lane badge (check circle for assistant/system results, bolt
 * for quick actions) + title + subline; actions row = Undo pill + Open
 * link. NO countdowns — Undo is visible while the 24h server window is
 * open and vanishes silently when it closes (a minute-tick re-render
 * keeps a long-mounted receipt honest). Undo failures render the
 * `mapUndoError` reason in place; a failed undo never silently reverts.
 *
 * Host contract (PR-6): render lists with `key={vm.requestId ?? …}` so
 * local undo state never leaks across recycled receipt rows.
 */
export function Receipt({
  vm,
  surface,
  onUndone,
}: {
  vm: ReceiptVM
  /** §9 surface tag for telemetry (e.g. 'dock', 'thread', 'today'). */
  surface: string
  /** Notifies the host after a confirmed server-side undo. */
  onUndone?: (vm: ReceiptVM) => void
}) {
  const { t } = useTranslation('receipts')
  const { t: tDash } = useTranslation('dashboard')
  const { getToken } = useAuth()
  // Local state only ever ADDS an undo the server hasn't echoed yet —
  // a server-sourced vm.undoneAt always wins (§6 server authority).
  const [localUndoneAt, setLocalUndoneAt] = useState<string | null>(null)
  const undoneAt = vm.undoneAt ?? localUndoneAt
  const [undoError, setUndoError] = useState<string | null>(null)
  const shownTracked = useRef(false)

  // Silent expiry (§6): re-evaluate the window once a minute so a
  // receipt that outlives the 24h window drops its Undo without a
  // reload. State is just a tick counter; the check itself is pure.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const live: ReceiptVM = { ...vm, undoneAt }
  const canUndo = undoableNow(live)

  // The ref makes this once-per-mount even though vm's identity changes
  // with parent renders — full deps stay lint-clean without refiring.
  useEffect(() => {
    if (canUndo && !shownTracked.current) {
      shownTracked.current = true
      trackUndoShown(vm, surface)
    }
  }, [canUndo, vm, surface])

  const undo = useMutation({
    mutationFn: async () => {
      if (!vm.requestId) throw new Error('no_request_id')
      trackUndoTapped(vm)
      return undoEricAction(getToken, vm.requestId)
    },
    onSuccess: () => {
      trackUndoResult(vm, true)
      setUndoError(null)
      const at = new Date().toISOString()
      setLocalUndoneAt(at)
      onUndone?.({ ...vm, undoneAt: at })
    },
    onError: (err) => {
      trackUndoResult(vm, false, undoErrorCode(err))
      setUndoError(mapUndoError(err, tDash))
    },
  })

  const isUndone = undoneAt !== null

  return (
    <div
      className={`rounded-2xl border px-3.5 py-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:[animation-duration:240ms] motion-safe:[animation-timing-function:cubic-bezier(0.23,1,0.32,1)] ${
        isUndone
          ? 'border-border bg-muted'
          : 'border-saved-line bg-background shadow-2'
      }`}
    >
      <div className="flex items-start gap-[11px]">
        {vm.lane === 'fast' ? (
          <span
            aria-hidden="true"
            className="grid size-[26px] shrink-0 place-items-center rounded-full border-[1.5px] border-saved bg-saved-soft text-saved-deep"
          >
            <Zap className="size-3.5" fill="currentColor" strokeWidth={0} />
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="grid size-[26px] shrink-0 place-items-center rounded-full bg-saved text-white"
          >
            <Check className="size-3.5" strokeWidth={3} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div
            className={`text-[15px] font-[620] ${
              isUndone ? 'text-muted-foreground line-through' : 'text-foreground'
            }`}
          >
            {vm.title}
          </div>
          {isUndone ? (
            <div className="text-[13px] text-muted-foreground">{t('undone')}</div>
          ) : undoError ? (
            <div className="text-[13px] text-destructive">{undoError}</div>
          ) : vm.sub ? (
            <div className="text-[13px] text-muted-foreground">{vm.sub}</div>
          ) : null}
        </div>
      </div>

      {!isUndone && (canUndo || vm.url) ? (
        <div className="ml-[37px] mt-[9px] flex items-center gap-2">
          {canUndo ? (
            <button
              type="button"
              onClick={() => undo.mutate()}
              disabled={undo.isPending}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-border px-[15px] text-[13px] font-[650] text-foreground transition-[background-color,transform] [transition-duration:120ms] active:scale-[0.97] disabled:opacity-50 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
            >
              <Undo2 aria-hidden="true" className="size-3.5" />
              {undo.isPending ? t('undoing') : t('undo')}
            </button>
          ) : null}
          {vm.url ? (
            <Link
              to={vm.url}
              className="inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-saved-deep [@media(hover:hover)_and_(pointer:fine)]:hover:text-saved-press [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
            >
              {t('open')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
